import React, { useCallback, useEffect, useState } from 'react';

interface MachineCalibration {
  equipmentId: number;
  name: string;
  samples: number;
  suggestedEfficiencyPct: number | null;
  currentEfficiencyPct: number | null;
  observedSetupMin: number | null;
  suggestedSetupMin: number | null;
  currentSetupMin: number | null;
  meanAbsErrorPct: number | null;
}

const fmt = (v: number | null, unit = '') => (v === null ? '—' : `${v}${unit}`);

const PlanningCalibration: React.FC = () => {
  const [rows, setRows] = useState<MachineCalibration[] | null>(null);
  const [minSamples, setMinSamples] = useState(3);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/planning/calibration', { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Error al cargar la calibración');
      const data = await res.json();
      setRows(data.machines);
      setMinSamples(data.minSamples);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (m: MachineCalibration) => {
    setApplying(m.equipmentId);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch('/api/planning/calibration/apply', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          equipmentId: m.equipmentId,
          ...(m.suggestedEfficiencyPct !== null ? { efficiencyPct: m.suggestedEfficiencyPct } : {}),
          ...(m.suggestedSetupMin !== null ? { setupMin: m.suggestedSetupMin } : {}),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Error al aplicar la calibración');
      setNotice(`Se actualizó el catálogo de ${m.name}.`);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setApplying(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-800">📈 Calibración con datos reales</h2>
        <p className="text-sm text-gray-500 mt-1">
          Compara la eficiencia y el setup del catálogo de equipos con lo que se registró en Ejecución de Planta (pasos completados).
          La sugerencia usa la mediana y requiere al menos {minSamples} pasos por equipo. Nada se aplica automáticamente.
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Cargando...</p>}
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
      {notice && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{notice}</p>}

      {rows && rows.length === 0 && !loading && (
        <p className="text-sm text-gray-500">Aún no hay pasos completados con tiempos registrados.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">Equipo</th>
                <th className="px-3 py-2 font-semibold">Pasos</th>
                <th className="px-3 py-2 font-semibold">Eficiencia catálogo → sugerida</th>
                <th className="px-3 py-2 font-semibold">Setup catálogo → sugerido (min)</th>
                <th className="px-3 py-2 font-semibold">Error del plan</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((m) => {
                const canApply = m.suggestedEfficiencyPct !== null || m.suggestedSetupMin !== null;
                return (
                  <tr key={m.equipmentId}>
                    <td className="px-3 py-2 font-medium">{m.name}</td>
                    <td className="px-3 py-2">{m.samples}</td>
                    <td className="px-3 py-2">{fmt(m.currentEfficiencyPct, '%')} → <strong>{fmt(m.suggestedEfficiencyPct, '%')}</strong></td>
                    <td className="px-3 py-2">{fmt(m.currentSetupMin)} → <strong>{fmt(m.suggestedSetupMin)}</strong></td>
                    <td className="px-3 py-2">{fmt(m.meanAbsErrorPct, '%')}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={!canApply || applying !== null}
                        onClick={() => apply(m)}
                        className="px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white disabled:opacity-40"
                      >
                        {applying === m.equipmentId ? 'Aplicando...' : 'Aplicar'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlanningCalibration;
